import { useState, useEffect } from 'react';
import { PlayingCard } from './PlayingCard';

interface Player {
  sessionId: string;
  displayName: string;
  seatIndex: number;
  team?: string;
  hand: string[];
}

interface SpadesTableProps {
  players: Player[];
  mySessionId: string | null;
  currentTurnPlayerId: string | null;
  phase: string;
  currentTrick: string[]; // Format: "sessionId:cardId"
  bids: Map<string, number>;
  tricksWon: Map<string, number>;
  team1Score: number;
  team1Bags: number;
  team2Score: number;
  team2Bags: number;
  team1Bid: number;
  team2Bid: number;
  team1Tricks: number;
  team2Tricks: number;
  roundNumber: number;
  spadesBroken: boolean;
  onBidSubmit: (bid: number) => void;
}

export function SpadesTable({
  players,
  mySessionId,
  currentTurnPlayerId,
  phase,
  currentTrick,
  bids,
  tricksWon,
  team1Score,
  team1Bags,
  team2Score,
  team2Bags,
  team1Bid,
  team2Bid,
  team1Tricks,
  team2Tricks,
  roundNumber,
  spadesBroken,
  onBidSubmit,
}: SpadesTableProps) {
  const isMyTurn = currentTurnPlayerId === mySessionId;
  const isBidding = phase === 'bidding';
  const myPlayer = players.find(p => p.sessionId === mySessionId);
  const serverBid = mySessionId ? bids.get(mySessionId) : undefined;
  const [submittedBid, setSubmittedBid] = useState<number | null>(null);

  // Reset local bid state when a new bidding round starts
  useEffect(() => {
    if (!isBidding) setSubmittedBid(null);
  }, [isBidding]);

  const myBid = serverBid ?? submittedBid ?? undefined;

  const handleBid = (bid: number) => {
    setSubmittedBid(bid);
    onBidSubmit(bid);
  };

  // Parse current trick cards
  const trickCards = currentTrick.map(entry => {
    const [sessionId, cardId] = entry.split(':');
    const player = players.find(p => p.sessionId === sessionId);
    return { sessionId, cardId, player };
  });

  // Position players around the table (bottom = me, then clockwise)
  const getPositionedPlayers = () => {
    const sorted = [...players].sort((a, b) => a.seatIndex - b.seatIndex);
    const myIndex = sorted.findIndex(p => p.sessionId === mySessionId);
    if (myIndex === -1) return { bottom: sorted[0], left: sorted[1], top: sorted[2], right: sorted[3] };

    // Rotate so I'm at the bottom
    const rotated = [...sorted.slice(myIndex), ...sorted.slice(0, myIndex)];
    return {
      bottom: rotated[0], // Me
      left: rotated[1],
      top: rotated[2],    // Partner (opposite)
      right: rotated[3],
    };
  };

  const positions = getPositionedPlayers();

  // Get card position in the trick display based on who played it
  const getCardPosition = (sessionId: string) => {
    if (sessionId === positions.bottom?.sessionId) return 'bottom';
    if (sessionId === positions.left?.sessionId) return 'left';
    if (sessionId === positions.top?.sessionId) return 'top';
    if (sessionId === positions.right?.sessionId) return 'right';
    return 'bottom';
  };

  const renderPlayerInfo = (player: Player | undefined, position: 'top' | 'bottom' | 'left' | 'right') => {
    if (!player) return null;

    const isCurrentTurn = player.sessionId === currentTurnPlayerId;
    const playerBid = bids.get(player.sessionId);
    const playerTricks = tricksWon.get(player.sessionId) ?? 0;
    const isMe = player.sessionId === mySessionId;
    const isPartner = player.team === myPlayer?.team && !isMe;

    return (
      <div className={`flex flex-col items-center gap-1 ${position === 'left' || position === 'right' ? 'w-24' : ''}`}>
        <div className={`px-3 py-2 rounded-lg text-center ${
          isCurrentTurn ? 'bg-yellow-600 ring-2 ring-yellow-400' :
          isMe ? 'bg-blue-600' :
          isPartner ? 'bg-blue-800' :
          'bg-shark-700'
        }`}>
          <p className="font-semibold text-sm truncate max-w-[100px]">{player.displayName}</p>
          {isBidding ? (
            <p className="text-xs text-gray-300">
              {playerBid !== undefined ? `Bid: ${playerBid}` : 'Bidding...'}
            </p>
          ) : (
            <p className="text-xs text-gray-300">
              {playerBid !== undefined ? `${playerTricks}/${playerBid}` : '-'}
            </p>
          )}
        </div>
        {/* Show cards in hand count */}
        <p className="text-xs text-gray-400">{player.hand.length} cards</p>
      </div>
    );
  };

  return (
    <div className="bg-green-900/50 rounded-xl p-4">
      {/* Header with scores */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-4">
          <div className={`px-3 py-1 rounded ${myPlayer?.team === 'team1' ? 'bg-blue-600' : 'bg-shark-700'}`}>
            <span className="text-sm font-medium">Team 1: </span>
            <span className="font-bold">{team1Score}</span>
            <span className="text-xs text-gray-300 ml-1">({team1Bags} bags)</span>
            {phase === 'playing' && <span className="text-xs ml-2">{team1Tricks}/{team1Bid}</span>}
          </div>
          <div className={`px-3 py-1 rounded ${myPlayer?.team === 'team2' ? 'bg-blue-600' : 'bg-shark-700'}`}>
            <span className="text-sm font-medium">Team 2: </span>
            <span className="font-bold">{team2Score}</span>
            <span className="text-xs text-gray-300 ml-1">({team2Bags} bags)</span>
            {phase === 'playing' && <span className="text-xs ml-2">{team2Tricks}/{team2Bid}</span>}
          </div>
        </div>
        <div className="text-sm text-gray-400">
          Round {roundNumber} {spadesBroken && <span className="text-blue-400 ml-2">Spades Broken</span>}
        </div>
      </div>

      {/* Table layout */}
      <div className="relative min-h-[350px]">
        {/* Top player */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2">
          {renderPlayerInfo(positions.top, 'top')}
        </div>

        {/* Left player */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2">
          {renderPlayerInfo(positions.left, 'left')}
        </div>

        {/* Right player */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2">
          {renderPlayerInfo(positions.right, 'right')}
        </div>

        {/* Bottom player (me) */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
          {renderPlayerInfo(positions.bottom, 'bottom')}
        </div>

        {/* Center trick area */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="relative w-48 h-48">
            {/* Trick cards */}
            {trickCards.map(({ sessionId, cardId }) => {
              const pos = getCardPosition(sessionId);
              const posStyles: Record<string, string> = {
                top: 'top-0 left-1/2 -translate-x-1/2',
                bottom: 'bottom-0 left-1/2 -translate-x-1/2',
                left: 'left-0 top-1/2 -translate-y-1/2',
                right: 'right-0 top-1/2 -translate-y-1/2',
              };
              return (
                <div key={cardId} className={`absolute ${posStyles[pos]}`}>
                  <PlayingCard cardId={cardId} size="sm" />
                </div>
              );
            })}

            {/* Empty center when no cards */}
            {trickCards.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-28 border-2 border-dashed border-green-700 rounded-lg flex items-center justify-center">
                  <span className="text-green-600 text-xs">Play area</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bidding UI */}
      {isBidding && isMyTurn && myBid === undefined && (
        <div className="mt-4 p-4 bg-yellow-900/50 rounded-lg ring-2 ring-yellow-500">
          <p className="text-center text-xl font-bold mb-3 text-yellow-400">YOUR TURN TO BID!</p>
          <p className="text-center text-sm text-gray-300 mb-3">How many tricks can you win? (minimum 1)</p>
          <div className="flex justify-center gap-2 flex-wrap">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map(bid => (
              <button
                key={bid}
                onClick={() => handleBid(bid)}
                className="w-12 h-12 rounded-lg font-bold text-lg transition-colors bg-blue-600 hover:bg-blue-700 hover:scale-110"
              >
                {bid}
              </button>
            ))}
          </div>
          <p className="text-center text-sm text-gray-400 mt-3">
            You have {myPlayer?.hand.length ?? 0} cards (jokers = highest spades)
          </p>
        </div>
      )}

      {/* Show when bidding but waiting */}
      {isBidding && myBid !== undefined && (
        <div className="mt-4 p-3 bg-shark-800 rounded-lg text-center">
          <p className="text-green-400">You bid {myBid} - waiting for others...</p>
        </div>
      )}

      {/* Turn indicator */}
      {phase === 'playing' && (
        <div className="mt-4 text-center">
          {isMyTurn ? (
            <p className="text-green-400 font-semibold">Your turn - click a card to play</p>
          ) : (
            <p className="text-gray-400">
              Waiting for {players.find(p => p.sessionId === currentTurnPlayerId)?.displayName}...
            </p>
          )}
        </div>
      )}

      {isBidding && !isMyTurn && (
        <div className="mt-4 text-center">
          <p className="text-gray-400">
            Waiting for {players.find(p => p.sessionId === currentTurnPlayerId)?.displayName} to bid...
          </p>
        </div>
      )}
    </div>
  );
}
